import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { Plus, Calendar, Lock } from 'lucide-react';
import toast from 'react-hot-toast';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const FiscalPeriods = () => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showYearModal, setShowYearModal] = useState(false);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [yearForm, setYearForm] = useState({ year: new Date().getFullYear(), start_date: '', end_date: '' });
  const [periodForm, setPeriodForm] = useState({ fiscal_year_id: '', period_number: 1, start_date: '', end_date: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [yearsRes, periodsRes] = await Promise.all([
        accountsApi.get('/fiscal-years'),
        accountsApi.get('/accounting-periods'),
      ]);
      setFiscalYears(yearsRes.data.fiscal_years);
      setPeriods(periodsRes.data.accounting_periods);
    } catch (err) {
      toast.error('Failed to load fiscal years/periods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreateYear = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await accountsApi.post('/fiscal-years', yearForm);
      toast.success('Fiscal year created');
      setShowYearModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create fiscal year');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePeriod = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await accountsApi.post('/accounting-periods', periodForm);
      toast.success('Accounting period created');
      setShowPeriodModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create accounting period');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClosePeriod = async (period) => {
    if (!window.confirm(`Close period ${MONTHS[period.period_number - 1]} ${period.start_date?.slice(0,4)}? Posted transactions before this stay visible, but no new posting will be allowed into it.`)) return;
    try {
      await accountsApi.put(`/accounting-periods/${period.id}/close`);
      toast.success('Period closed');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to close period');
    }
  };

  return (
    <Layout title="Fiscal Years & Periods">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">A posting must fall inside an Open accounting period. Closing a period only blocks new postings into it — history stays visible.</p>
        <div className="flex gap-2">
          <button onClick={() => setShowYearModal(true)}
            className="flex items-center gap-2 bg-white border border-blue-800 text-blue-800 px-4 py-2 rounded-lg text-sm hover:bg-blue-50">
            <Plus size={16} /> New Fiscal Year
          </button>
          <button onClick={() => setShowPeriodModal(true)}
            className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
            <Plus size={16} /> New Period
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : fiscalYears.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">No fiscal years yet — create one to get started.</div>
      ) : (
        fiscalYears.map(fy => (
          <div key={fy.id} className="mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Calendar size={18} className="text-blue-800" />
              FY {fy.year}
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${fy.status === 'Open' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{fy.status}</span>
              <span className="text-sm font-normal text-gray-500">{fy.start_date?.slice(0,10)} → {fy.end_date?.slice(0,10)}</span>
            </h2>
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Period</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Start</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">End</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.filter(p => p.fiscal_year_id === fy.id).length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-4 text-gray-400">No periods yet</td></tr>
                  ) : periods.filter(p => p.fiscal_year_id === fy.id).map(p => (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{MONTHS[p.period_number - 1]} (#{p.period_number})</td>
                      <td className="px-4 py-3 text-gray-600">{p.start_date?.slice(0,10)}</td>
                      <td className="px-4 py-3 text-gray-600">{p.end_date?.slice(0,10)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.status === 'Open' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {p.status === 'Open' && (
                          <button onClick={() => handleClosePeriod(p)} className="flex items-center gap-1 text-red-600 hover:text-red-800 text-xs font-medium mx-auto">
                            <Lock size={12} /> Close
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {showYearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold">New Fiscal Year</h2>
              <button onClick={() => setShowYearModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreateYear} className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
                <input required type="number" value={yearForm.year}
                  onChange={(e) => setYearForm({ ...yearForm, year: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                  <input required type="date" value={yearForm.start_date}
                    onChange={(e) => setYearForm({ ...yearForm, start_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                  <input required type="date" value={yearForm.end_date}
                    onChange={(e) => setYearForm({ ...yearForm, end_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowYearModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPeriodModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold">New Accounting Period</h2>
              <button onClick={() => setShowPeriodModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreatePeriod} className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fiscal Year</label>
                <select required value={periodForm.fiscal_year_id}
                  onChange={(e) => setPeriodForm({ ...periodForm, fiscal_year_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select fiscal year...</option>
                  {fiscalYears.map(fy => <option key={fy.id} value={fy.id}>FY {fy.year}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Period (Month)</label>
                <select value={periodForm.period_number}
                  onChange={(e) => setPeriodForm({ ...periodForm, period_number: Number(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m} (#{i + 1})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                  <input required type="date" value={periodForm.start_date}
                    onChange={(e) => setPeriodForm({ ...periodForm, start_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                  <input required type="date" value={periodForm.end_date}
                    onChange={(e) => setPeriodForm({ ...periodForm, end_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowPeriodModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FiscalPeriods;
